import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Spinner } from 'components/UI';
import styles from './MonthlyNetSalesLine.module.scss';
import type { MonthlyNetSalesReport } from 'model/MonthlyNetSalesReport';

interface Props {
    report: MonthlyNetSalesReport;
    loading: boolean;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SERIES_COLORS = [
    '#5E8677',
    '#92B4A7',
    '#BAA997',
    '#C18733',
    '#EAB464',
    '#B2B2B2',
    '#ECC915',
    '#61CE8D',
    '#6C6C6C',
    '#000000',
    '#8A5E34',
    '#4C7C9E'
];

export const MonthlyNetSalesLineChart: React.FC<Props> = ({ report, loading }) => {
    const data = useMemo(() => {
        if (!report?.rows?.length) return [];

        const months = Array.from({ length: 12 }, (_, i) => i + 1);

        return months.map((month, idx) => {
            const rowValues: Record<string, number> = {};
            report.rows.forEach(row => {
                const match = row.months.find(m => m.month === month);
                rowValues[row.key] = Number(match?.netSales ?? 0);
            });

            return {
                month: `${MONTH_LABELS[idx]} ${report.year}`,
                ...rowValues
            };
        });
    }, [report]);

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <Spinner />
            </div>
        );
    }

    if (!report?.rows?.length) {
        return <div className={styles.empty}>No data</div>;
    }

    return (
        <div className={styles.chartCard}>
            <div className={styles.legend}>
                {report.rows.map((row, idx) => (
                    <div
                        key={row.key}
                        className={styles.legendItem}
                    >
                        <span
                            className={styles.legendDot}
                            style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                        />
                        <span className={styles.legendLabel}>{row.label}</span>
                    </div>
                ))}
            </div>
            <ResponsiveContainer
                width="100%"
                height={420}
            >
                <LineChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                >
                    <CartesianGrid
                        stroke="#eee"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="month"
                        tick={{ fill: '#6b6b6b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tickMargin={12}
                    />
                    <YAxis
                        tick={{ fill: '#6b6b6b', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={v => `$${Number(v).toLocaleString('en-US')}`}
                    />
                    <Tooltip formatter={(value: number) => `$${Number(value).toLocaleString('en-US')}`} />
                    {report.rows.map((row, idx) => (
                        <Line
                            key={row.key}
                            type="monotone"
                            dataKey={row.key}
                            name={row.label}
                            stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                            dot={{ r: 3 }}
                            strokeWidth={2}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MonthlyNetSalesLineChart;
